import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDashLang } from "@/context/DashboardLangContext";
import UniversalSearch from "@/components/UniversalSearch";
import { listRecentActivity, type NotificationEvent } from "@/lib/supabase/notifications";

const NOTIF_STYLE: Record<NotificationEvent["severity"], { icon: string; color: string }> = {
  info:    { icon: "ri-information-line",   color: "text-gold-ink" },
  success: { icon: "ri-check-line",         color: "text-green-600" },
  warning: { icon: "ri-alert-line",         color: "text-orange-600" },
  error:   { icon: "ri-error-warning-line", color: "text-red-600" },
};

/** Per-org "last opened the bell" timestamp — events newer than it count as unread. */
const seenKey = (orgId: string) => `diyafa:notif-seen:${orgId}`;
function readSeen(orgId: string): string {
  try { return localStorage.getItem(seenKey(orgId)) ?? ""; } catch { return ""; }
}
function writeSeen(orgId: string, iso: string) {
  try { localStorage.setItem(seenKey(orgId), iso); } catch { /* storage unavailable */ }
}

const pageTitles: Record<string, [string, string]> = {
  "/dashboard":              ["Dashboard",       "لوحة التحكم"],
  "/dashboard/checkin":      ["Guest Check-In",  "تسجيل الوصول"],
  "/dashboard/walkin":       ["Walk-In",         "ضيف مباشر"],
  "/dashboard/checkout":     ["Check-Out",       "تسجيل المغادرة"],
  "/dashboard/reservations": ["Reservations",    "الحجوزات"],
  "/dashboard/guests":       ["Guests",          "النزلاء"],
  "/dashboard/rooms":        ["Rooms",           "الغرف"],
  "/dashboard/new-rental":   ["New Rental",      "تأجير جديد"],
  "/dashboard/vehicle-return":["Vehicle Return", "إعادة المركبة"],
  "/dashboard/customers":    ["Customers",       "العملاء"],
  "/dashboard/housekeeping": ["Housekeeping",    "التدبير المنزلي"],
  "/dashboard/billing":      ["Billing",         "الفواتير"],
  "/dashboard/reports":      ["Reports",         "التقارير"],
  "/dashboard/rental-reports":["Rental Reports", "تقارير التأجير"],
  "/dashboard/staff":        ["Staff",           "الموظفون"],
  "/dashboard/notifications":["Notifications",   "الإشعارات"],
  "/dashboard/batch-sync":   ["Batch & Sync",    "المزامنة"],
  "/dashboard/rasad":        ["Rasad",           "رصد"],
  "/dashboard/vehicles":     ["Vehicles",        "المركبات"],
  "/dashboard/billing-settings":["Plan & Billing", "الخطة والفوترة"],
  "/dashboard/activity":     ["Activity Log",    "سجل النشاط"],
};

interface TopBarProps {
  /** Mobile hamburger handler — opens the Sidebar drawer. */
  onMobileMenuOpen?: () => void;
}

export default function TopBar({ onMobileMenuOpen }: TopBarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, currentOrg } = useAuth();
  const { isAr, toggleLang, t } = useDashLang();

  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);
  const [seenAt, setSeenAt] = useState("");

  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // Exact match first, then the closest parent (e.g. /dashboard/reservations/:id
  // → "Reservations").
  const titleKey = Object.keys(pageTitles)
    .filter((p) => location.pathname === p || location.pathname.startsWith(p + "/"))
    .sort((a, b) => b.length - a.length)[0];
  const titlePair = (titleKey && pageTitles[titleKey]) ?? ["Dashboard", "لوحة التحكم"];
  const title = isAr ? titlePair[1] : titlePair[0];

  const displayName =
    user?.user_metadata?.full_name ||
    user?.email?.split("@")[0] ||
    "User";

  const initials = displayName
    .split(" ")
    .slice(0, 2)
    .map((w: string) => w[0]?.toUpperCase() ?? "")
    .join("");

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate("/");
  };

  // Bell shows the latest real activity (same feed as /dashboard/notifications),
  // refreshed on mount, org switch and every time the dropdown opens.
  useEffect(() => {
    if (!currentOrg) return;
    setSeenAt(readSeen(currentOrg.id));
    let cancelled = false;
    listRecentActivity(currentOrg.id, 5)
      .then((events) => { if (!cancelled) setNotifications(events); })
      .catch(() => { if (!cancelled) setNotifications([]); });
    return () => { cancelled = true; };
  }, [currentOrg, notifOpen]);

  const isUnread = (n: NotificationEvent) => !seenAt || n.ts > seenAt;
  const unreadCount = notifications.filter(isUnread).length;

  const markAllRead = () => {
    if (!currentOrg) return;
    const now = new Date().toISOString();
    writeSeen(currentOrg.id, now);
    setSeenAt(now);
  };

  const timeAgo = (iso: string) => {
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
    if (mins < 60) return t(`${mins}m ago`, `منذ ${mins} دقيقة`);
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return t(`${hrs}h ago`, `منذ ${hrs} ساعة`);
    const days = Math.round(hrs / 24);
    return t(`${days}d ago`, `منذ ${days} يوم`);
  };

  return (
    <header
      className="h-14 bg-white border-b border-line flex items-center justify-between px-3 sm:px-6 flex-shrink-0 gap-2"
      dir={isAr ? "rtl" : "ltr"}
    >
      {/* Left: hamburger (mobile) + title */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        {/* Hamburger — shown below lg because the docked sidebar
         *  vanishes into an off-canvas drawer there. */}
        {onMobileMenuOpen && (
          <button
            onClick={onMobileMenuOpen}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-ink-soft hover:bg-cream cursor-pointer flex-shrink-0"
            aria-label={t("Open navigation", "فتح القائمة")}
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
        )}
        <h1 className={`font-semibold text-ink text-sm truncate ${isAr ? "font-tajawal" : ""}`}>
          {title}
        </h1>
        {/* Rasad sync pill removed in sprint 0 — was showing a fake green
         *  "Synced ✓" tied to no real integration. Will reintroduce in
         *  sprint 4 with live state (syncing / synced / error / not-configured)
         *  once we wire up the Rasad compliance API. A lying UI element
         *  is worse than an absent one. */}
        {/* Build version — single source of truth from /version.json, injected
         *  at build time by vite.config.ts. Hover shows commit + build time
         *  for bug reports. Always LTR so SemVer isn't mirrored in RTL. */}
        <span
          className="hidden md:inline-flex items-center gap-1 text-[10px] text-muted font-mono px-2 py-0.5 rounded border border-line bg-cream"
          dir="ltr"
          title={`Build ${__APP_VERSION_META__.commit} · ${__APP_VERSION_META__.builtAt}`}
        >
          <i className="ri-git-commit-line text-gold" />
          <span>v{__APP_VERSION__}</span>
        </span>
      </div>

      {/* v1.22.12 — Universal search (Cmd+K anywhere in the app). */}
      <UniversalSearch />

      {/* Right actions */}
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">

        {/* v1.22.12 — Clickable search pill. Opens the same Cmd+K
             overlay via a custom event. Keyboard shortcut hint shown. */}
        <button
          onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
          className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-line-strong bg-white text-xs text-muted hover:bg-cream hover:text-ink-soft cursor-pointer transition-colors ${isAr ? "font-tajawal" : ""}`}
          aria-label={t("Search", "بحث")}
        >
          <i className="ri-search-line text-gold"></i>
          <span>{t("Search…", "بحث…")}</span>
          <kbd className="px-1 py-0.5 rounded border border-line-strong bg-cream text-[10px] font-mono">⌘K</kbd>
        </button>

        {/* Lang toggle — icon-only on mobile, icon+label from sm up. */}
        <button
          onClick={toggleLang}
          className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg border border-line-strong text-xs font-medium text-ink-soft hover:bg-cream cursor-pointer transition-colors whitespace-nowrap"
          aria-label={t("Toggle language", "تبديل اللغة")}
        >
          <i className="ri-translate-2 text-sm"></i>
          <span className="hidden sm:inline">{isAr ? "English" : "عربي"}</span>
        </button>

        {/* v1.22.11 — Help / support link. Replaces the Readdy floating
             widget on dashboard routes so it can't collide with form
             submit buttons. Same phone/email the landing page uses. */}
        <a
          href="mailto:support@diyafa.tech?subject=Diyafa%20Support%20Request"
          title={t("Talk to Support", "تواصل مع الدعم")}
          className="hidden sm:flex w-9 h-9 items-center justify-center rounded-lg hover:bg-cream cursor-pointer transition-colors"
          aria-label={t("Help", "المساعدة")}
        >
          <i className="ri-customer-service-2-line text-ink-soft text-lg"></i>
        </a>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen(!notifOpen); setUserMenuOpen(false); }}
            className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-cream cursor-pointer transition-colors"
            aria-label={t("Notifications", "الإشعارات")}
          >
            <i className="ri-notification-3-line text-ink-soft text-lg"></i>
            {unreadCount > 0 && <span className="absolute top-1 end-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{unreadCount > 9 ? "9+" : unreadCount}</span>}
          </button>
          {notifOpen && (
            <div className={`absolute top-11 w-72 bg-white rounded-2xl border border-line z-50 overflow-hidden ${isAr ? "left-0" : "right-0"}`}>
              <div className="px-4 py-3 border-b border-line flex items-center justify-between">
                <span className={`text-sm font-semibold text-ink ${isAr ? "font-tajawal" : ""}`}>
                  {t("Notifications", "الإشعارات")}
                </span>
                <button onClick={markAllRead} className={`text-xs text-gold cursor-pointer font-medium hover:underline ${isAr ? "font-tajawal" : ""}`}>
                  {t("Mark all read", "تعليم الكل كمقروء")}
                </button>
              </div>
              {notifications.length === 0 && (
                <p className={`px-4 py-6 text-center text-xs text-muted ${isAr ? "font-tajawal" : ""}`}>
                  {t("No recent activity yet.", "لا يوجد نشاط حديث بعد.")}
                </p>
              )}
              {notifications.map((n) => {
                const style = NOTIF_STYLE[n.severity];
                const unread = isUnread(n);
                return (
                  <button key={n.id} type="button"
                    onClick={() => { navigate("/dashboard/notifications"); setNotifOpen(false); }}
                    className={`w-full text-start flex items-start gap-3 px-4 py-3 hover:bg-cream cursor-pointer border-b border-line last:border-0 ${unread ? "bg-cream/60" : ""}`}>
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cream flex-shrink-0">
                      <i className={`${style.icon} ${style.color} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-snug truncate ${unread ? "text-ink font-medium" : "text-ink-soft"}`}>{n.title}</p>
                      <p className={`text-[10px] text-muted mt-0.5 ${isAr ? "font-tajawal" : ""}`}>{timeAgo(n.ts)}</p>
                    </div>
                    {unread && <div className="w-2 h-2 rounded-full bg-gold flex-shrink-0 mt-1"></div>}
                  </button>
                );
              })}
              <div className="px-4 py-2.5 border-t border-line">
                <button
                  onClick={() => { navigate("/dashboard/notifications"); setNotifOpen(false); }}
                  className={`text-xs text-gold font-medium cursor-pointer hover:underline ${isAr ? "font-tajawal" : ""}`}
                >
                  {t("View all notifications →", "عرض كل الإشعارات ←")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative" ref={userRef}>
          <button
            onClick={() => { setUserMenuOpen(!userMenuOpen); setNotifOpen(false); }}
            className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-cream cursor-pointer transition-colors group"
          >
            <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">{initials || "U"}</span>
            </div>
            <div className={`hidden sm:block text-left ${isAr ? "text-right" : ""}`}>
              <p className={`text-xs font-semibold text-ink leading-tight max-w-[120px] truncate ${isAr ? "font-tajawal" : ""}`}>
                {displayName}
              </p>
              <p className="text-[10px] text-muted leading-tight max-w-[120px] truncate">
                {user?.email}
              </p>
            </div>
            <i className="ri-arrow-down-s-line text-muted text-sm"></i>
          </button>

          {userMenuOpen && (
            <div className={`absolute top-12 w-56 bg-white rounded-2xl border border-line z-50 overflow-hidden ${isAr ? "left-0" : "right-0"}`}>
              {/* User info header */}
              <div className="px-4 py-3 border-b border-line bg-cream">
                <p className={`text-xs font-semibold text-ink truncate ${isAr ? "font-tajawal" : ""}`}>{displayName}</p>
                <p className="text-[10px] text-muted truncate">{user?.email}</p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => { navigate("/dashboard"); setUserMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ink-soft hover:bg-cream cursor-pointer transition-colors ${isAr ? "font-tajawal" : ""}`}
                >
                  <i className="ri-dashboard-line text-gold text-base flex-shrink-0"></i>
                  {t("Dashboard", "لوحة التحكم")}
                </button>
                <button
                  onClick={() => { navigate("/"); setUserMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ink-soft hover:bg-cream cursor-pointer transition-colors ${isAr ? "font-tajawal" : ""}`}
                >
                  <i className="ri-home-line text-gold text-base flex-shrink-0"></i>
                  {t("Back to Website", "العودة للموقع")}
                </button>
              </div>

              <div className="border-t border-line py-1">
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-colors disabled:opacity-60 ${isAr ? "font-tajawal" : ""}`}
                >
                  {signingOut ? (
                    <i className="ri-loader-4-line animate-spin text-base flex-shrink-0"></i>
                  ) : (
                    <i className="ri-logout-box-r-line text-base flex-shrink-0"></i>
                  )}
                  {t("Sign Out", "تسجيل الخروج")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
